// Tenantra Inc. - Landing "Built on trusted infrastructure" partner honeycomb
import { useEffect, useRef, useState } from 'react';
import { LogoMark } from '@/components/Logo';

const W = 150, H = 130, G = 8;
const HEX = 'polygon(25% 0, 75% 0, 100% 50%, 75% 100%, 25% 100%, 0 50%)';
const X = [0, 0.75 * W + G, 1.5 * W + 2 * G];
const PARTNERS = [
  { id: 'equifax', name: 'EQUIFAX', role: 'Credit reporting', cls: 'font-bold tracking-[0.08em] text-[#9E1B32]', x: X[1], y: 0 },
  { id: 'payments', name: 'Zūm Rails', role: 'Payments', cls: 'font-bold tracking-tight text-stone-900', x: X[0], y: H / 2 + G / 2 },
  { id: 'didit', name: 'didit', role: 'ID verification', cls: 'font-bold tracking-tight text-stone-900', x: X[2], y: H / 2 + G / 2 },
  { id: 'signwell', name: 'SignWell', role: 'E-signatures', cls: 'font-semibold text-[#0C6E4F]', x: X[0], y: 1.5 * H + 1.5 * G },
  { id: 'stripe', name: 'stripe', role: 'Billing', cls: 'font-bold tracking-tight text-[#635BFF]', x: X[2], y: 1.5 * H + 1.5 * G },
  { id: 'postmark', name: 'Postmark', role: 'Email delivery', cls: 'font-semibold text-[#2B2A2A]', x: X[1], y: 2 * H + 2 * G },
];

function Hex({ x, y, delay, show, children, center, testId }) {
  return (
    <div className={`absolute group transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${show ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`} style={{ left: x, top: y, width: W, height: H, transitionDelay: `${delay}ms` }} data-testid={testId}>
      <div className={`w-full h-full p-px transition-transform duration-300 group-hover:-translate-y-1 ${center ? 'bg-teal-600' : 'bg-stone-200 group-hover:bg-teal-500'}`} style={{ clipPath: HEX }}>
        <div className={`w-full h-full flex flex-col items-center justify-center gap-1.5 transition-colors duration-300 ${center ? 'bg-teal-700' : 'bg-[#FAF8F5] group-hover:bg-white'}`} style={{ clipPath: HEX }}>{children}</div>
      </div>
    </div>
  );
}

export function PartnersSection() {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  const [k, setK] = useState(1);
  useEffect(() => { const f = () => setK(window.innerWidth < 640 ? 0.72 : 1); f(); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShow(true); io.disconnect(); } }, { threshold: 0.2 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <section ref={ref} id="partners" className="py-12 sm:py-16 lg:py-20 bg-white border-t border-stone-200" data-testid="partners-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="flex justify-center order-2 lg:order-1 min-w-0">
          <div style={{ width: (X[2] + W) * k, height: (3 * H + 2 * G) * k }}>
          <div className="relative origin-top-left" style={{ width: X[2] + W, height: 3 * H + 2 * G, transform: `scale(${k})` }} data-testid="partners-honeycomb">
            <Hex x={X[1]} y={H + G} delay={0} show={show} center testId="partner-tenantra">
              <LogoMark className="w-8 h-8" color="#5EEAD4" /><span className="text-white font-bold tracking-tight text-[15px]" style={{ fontFamily: "'Chivo', sans-serif" }}>Tenantra</span>
            </Hex>
            {PARTNERS.map((p, i) => (
              <Hex key={p.id} x={p.x} y={p.y} delay={150 + i * 90} show={show} testId={`partner-${p.id}`}>
                <span className={`text-lg leading-none ${p.cls}`}>{p.name}</span>
                <span className="text-[10px] uppercase tracking-[0.14em] text-stone-500 group-hover:text-teal-700 transition-colors">{p.role}</span>
              </Hex>
            ))}
          </div>
          </div>
        </div>
        <div className={`min-w-0 order-1 lg:order-2 text-center lg:text-left transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 mb-3 sm:mb-4">Our Infrastructure</p>
          <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-3 text-stone-900">
            Trusted partners. <span className="text-teal-600">Secure by design.</span>
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-stone-700 max-w-xl mx-auto lg:mx-0">
            Tenantra runs on established providers for credit reporting, payments, identity verification and e-signatures, so every report, payment and consent is handled to industry standards.
          </p>
        </div>
      </div>
    </section>
  );
}
